/**
 * Quest Unlock Condition Evaluator
 *
 * Flexible system for checking if quests are available based on player state.
 * Supports 20+ different condition types for maximum extensibility.
 */

import {
  QuestDefinition,
  QuestUnlockConditions,
  PlayerState,
  Quality,
} from '@/types';

/**
 * Evaluate if a quest is unlocked for the player
 * @returns true if quest is available, false otherwise
 */
export function evaluateQuestUnlock(
  quest: QuestDefinition,
  playerState: PlayerState
): boolean {
  // If no unlock conditions, quest is always available
  if (!quest.unlockConditions) {
    return true;
  }

  const conditions = quest.unlockConditions;

  // Check each condition type
  return (
    checkLevelRequirements(conditions, playerState) &&
    checkCurrencyRequirements(conditions, playerState) &&
    checkFishCaughtRequirements(conditions, playerState) &&
    checkFishSoldRequirements(conditions, playerState) &&
    checkEquipmentRequirements(conditions, playerState) &&
    checkBuddyRequirements(conditions, playerState) &&
    checkZoneRequirements(conditions, playerState) &&
    checkQuestDependencies(conditions, playerState) &&
    checkTimeRequirements(conditions) &&
    checkCustomConditions(conditions, playerState)
  );
}

/**
 * Check level requirements
 */
function checkLevelRequirements(
  conditions: QuestUnlockConditions,
  playerState: PlayerState
): boolean {
  if (conditions.minLevel !== undefined) {
    if (playerState.progression.progress.level < conditions.minLevel) {
      return false;
    }
  }

  return true;
}

/**
 * Check currency requirements
 */
function checkCurrencyRequirements(
  conditions: QuestUnlockConditions,
  playerState: PlayerState
): boolean {
  if (conditions.minCurrency !== undefined) {
    if (playerState.currency < conditions.minCurrency) {
      return false;
    }
  }

  if (conditions.maxCurrency !== undefined) {
    if (playerState.currency > conditions.maxCurrency) {
      return false;
    }
  }

  return true;
}

/**
 * Check fish caught requirements
 */
function checkFishCaughtRequirements(
  conditions: QuestUnlockConditions,
  playerState: PlayerState
): boolean {
  // Total fish caught
  if (conditions.minFishCaught !== undefined) {
    if (playerState.stats.totalFishCaught < conditions.minFishCaught) {
      return false;
    }
  }

  // Fish caught by species
  if (conditions.minFishCaughtBySpecies) {
    for (const [speciesId, requiredCount] of Object.entries(
      conditions.minFishCaughtBySpecies
    )) {
      const caughtCount = playerState.stats.fishCaughtBySpecies[speciesId] || 0;
      if (caughtCount < requiredCount) {
        return false;
      }
    }
  }

  // Fish caught by quality
  if (conditions.minFishCaughtByQuality) {
    for (const [quality, requiredCount] of Object.entries(
      conditions.minFishCaughtByQuality
    )) {
      const caughtCount =
        playerState.stats.fishCaughtByQuality[quality as Quality] || 0;
      if (caughtCount < requiredCount) {
        return false;
      }
    }
  }

  // Largest fish weight
  if (conditions.minLargestFishWeight !== undefined) {
    const largestWeight = Math.max(
      ...playerState.discoveredFish.map((f) => f.records.maxWeight),
      0
    );
    if (largestWeight < conditions.minLargestFishWeight) {
      return false;
    }
  }

  // Largest fish length
  if (conditions.minLargestFishLength !== undefined) {
    const largestLength = Math.max(
      ...playerState.discoveredFish.map((f) => f.records.maxLength),
      0
    );
    if (largestLength < conditions.minLargestFishLength) {
      return false;
    }
  }

  return true;
}

/**
 * Check fish sold requirements
 */
function checkFishSoldRequirements(
  conditions: QuestUnlockConditions,
  playerState: PlayerState
): boolean {
  if (conditions.minFishSold !== undefined) {
    if (playerState.stats.totalFishSold < conditions.minFishSold) {
      return false;
    }
  }

  if (conditions.minCurrencyEarned !== undefined) {
    if (playerState.stats.totalCurrencyEarned < conditions.minCurrencyEarned) {
      return false;
    }
  }

  return true;
}

/**
 * Check equipment requirements
 */
function checkEquipmentRequirements(
  conditions: QuestUnlockConditions,
  playerState: PlayerState
): boolean {
  // Owned equipment
  if (conditions.ownedEquipment) {
    for (const equipmentId of conditions.ownedEquipment) {
      if (!playerState.ownedEquipment[equipmentId]) {
        return false;
      }
    }
  }

  // Equipped equipment
  if (conditions.equippedEquipment) {
    const equippedIds = Object.values(playerState.equippedGear).filter(
      (id) => id !== null
    );
    for (const equipmentId of conditions.equippedEquipment) {
      if (!equippedIds.includes(equipmentId)) {
        return false;
      }
    }
  }

  return true;
}

/**
 * Check buddy requirements
 */
function checkBuddyRequirements(
  conditions: QuestUnlockConditions,
  playerState: PlayerState
): boolean {
  // Minimum buddy count
  if (conditions.minBuddyCount !== undefined) {
    const hiredFishingBuddies = playerState.fishingBuddies.filter(
      (b) => b.hired
    ).length;
    const hiredHaulerBuddies = playerState.haulerBuddies.filter(
      (b) => b.hired
    ).length;
    const totalBuddies = hiredFishingBuddies + hiredHaulerBuddies;

    if (totalBuddies < conditions.minBuddyCount) {
      return false;
    }
  }

  // Specific buddies unlocked
  if (conditions.unlockedBuddies) {
    // Check fishing buddies
    const fishingBuddyIds = playerState.fishingBuddies
      .filter((b) => b.hired)
      .map((b) => `fishing_buddy_${b.slotNumber}`);

    // Check hauler buddies
    const haulerBuddyIds = playerState.haulerBuddies
      .filter((b) => b.hired)
      .map((b) => `hauler_buddy_${b.slotNumber}`);

    const allBuddyIds = [...fishingBuddyIds, ...haulerBuddyIds];

    for (const buddyId of conditions.unlockedBuddies) {
      if (!allBuddyIds.includes(buddyId)) {
        return false;
      }
    }
  }

  return true;
}

/**
 * Check zone requirements
 */
function checkZoneRequirements(
  conditions: QuestUnlockConditions,
  playerState: PlayerState
): boolean {
  // Unlocked zones
  if (conditions.unlockedZones) {
    for (const zoneId of conditions.unlockedZones) {
      if (!playerState.unlockedZones.includes(zoneId)) {
        return false;
      }
    }
  }

  // Current zone
  if (conditions.currentZone) {
    if (playerState.currentZone !== conditions.currentZone) {
      return false;
    }
  }

  return true;
}

/**
 * Check quest dependencies
 */
function checkQuestDependencies(
  conditions: QuestUnlockConditions,
  playerState: PlayerState
): boolean {
  // Completed quests
  if (conditions.completedQuests) {
    for (const questId of conditions.completedQuests) {
      if (!playerState.quests.completedQuests.includes(questId)) {
        return false;
      }
    }
  }

  // Active quests
  if (conditions.activeQuests) {
    const activeQuestIds = playerState.quests.activeQuests.map((q) => q.questId);
    for (const questId of conditions.activeQuests) {
      if (!activeQuestIds.includes(questId)) {
        return false;
      }
    }
  }

  // Failed quests (quest becomes available if these failed)
  if (conditions.failedQuests) {
    for (const questId of conditions.failedQuests) {
      if (!playerState.quests.failedQuests.includes(questId)) {
        return false;
      }
    }
  }

  return true;
}

/**
 * Check time-based requirements
 */
function checkTimeRequirements(conditions: QuestUnlockConditions): boolean {
  const now = Date.now();

  // Available after
  if (conditions.availableAfter !== undefined) {
    if (now < conditions.availableAfter) {
      return false;
    }
  }

  // Available before
  if (conditions.availableBefore !== undefined) {
    if (now > conditions.availableBefore) {
      return false;
    }
  }

  return true;
}

/**
 * Check custom conditions
 * Allows for complex custom logic via registered functions
 */
function checkCustomConditions(
  conditions: QuestUnlockConditions,
  playerState: PlayerState
): boolean {
  if (!conditions.customCondition) {
    return true;
  }

  // Look up custom condition function
  const customFn = CUSTOM_CONDITION_REGISTRY[conditions.customCondition];

  if (!customFn) {
    console.warn(
      `Custom condition "${conditions.customCondition}" not found in registry`
    );
    return false;
  }

  return customFn(playerState);
}

/**
 * Custom condition function registry
 * Allows defining complex custom logic for quest unlocks
 */
export const CUSTOM_CONDITION_REGISTRY: Record<
  string,
  (playerState: PlayerState) => boolean
> = {
  /**
   * Example: Quest available only on weekends
   */
  isWeekend: () => {
    const day = new Date().getDay();
    return day === 0 || day === 6; // Sunday or Saturday
  },

  /**
   * Example: Quest available if player has caught all common fish
   */
  hasAllCommonFish: (playerState) => {
    // This would need to check against fish definitions
    // Placeholder logic
    return playerState.discoveredFish.length >= 5;
  },

  /**
   * Example: Quest available if player has full inventory
   */
  hasFullInventory: (playerState) => {
    return (
      playerState.inventory.currentCount >= playerState.inventory.capacity
    );
  },

  // Add more custom conditions as needed...
};

/**
 * Register a custom condition function
 * Allows dynamically adding new condition types
 */
export function registerCustomCondition(
  name: string,
  fn: (playerState: PlayerState) => boolean
): void {
  CUSTOM_CONDITION_REGISTRY[name] = fn;
}

/**
 * Batch evaluate multiple quests
 * Returns array of available quest IDs
 */
export function getAvailableQuests(
  quests: QuestDefinition[],
  playerState: PlayerState
): string[] {
  return quests
    .filter((quest) => evaluateQuestUnlock(quest, playerState))
    .map((quest) => quest.id);
}

/**
 * Get reason why quest is locked (for UI tooltips)
 * Returns human-readable string or null if unlocked
 */
export function getQuestLockReason(
  quest: QuestDefinition,
  playerState: PlayerState
): string | null {
  if (!quest.unlockConditions) {
    return null; // No conditions = always unlocked
  }

  const conditions = quest.unlockConditions;

  // Check each condition and return first failure reason
  if (conditions.minLevel !== undefined) {
    if (playerState.progression.progress.level < conditions.minLevel) {
      return `Requires level ${conditions.minLevel}`;
    }
  }

  if (conditions.minCurrency !== undefined) {
    if (playerState.currency < conditions.minCurrency) {
      return `Requires ${conditions.minCurrency} gold`;
    }
  }

  if (conditions.minFishCaught !== undefined) {
    if (playerState.stats.totalFishCaught < conditions.minFishCaught) {
      return `Catch ${conditions.minFishCaught} fish`;
    }
  }

  if (conditions.completedQuests && conditions.completedQuests.length > 0) {
    for (const questId of conditions.completedQuests) {
      if (!playerState.quests.completedQuests.includes(questId)) {
        return `Complete previous quest first`;
      }
    }
  }

  // If all checks pass but evaluateQuestUnlock returns false,
  // there must be a more complex condition
  if (!evaluateQuestUnlock(quest, playerState)) {
    return `Requirements not met`;
  }

  return null; // Quest is unlocked
}
